import { useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { createTruckMarker, createPulsingDot, calculateBounds } from '@/config/mapbox.config';
import { DashboardOperation } from '@/types/dashboard.types';

// Explicitly import DOM types to satisfy the linter
type HTMLDivElement = globalThis.HTMLDivElement;

interface UseMapOperationsProps {
  operations: DashboardOperation[];
  onMarkerClick: (operationId: string) => void;
}

export const useMapOperations = ({ operations, onMarkerClick }: UseMapOperationsProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Function to remove pulsing effect from a marker
  const removePulsingEffect = (marker: mapboxgl.Marker) => {
    const markerElement = marker.getElement();
    const pulsingDot = markerElement.querySelector('div > canvas');
    if (pulsingDot?.parentElement) {
      pulsingDot.parentElement.remove();
    }
  };

  // Function to remove pulsing effects from all markers
  const removeAllPulsingEffects = useCallback(() => {
    markersRef.current.forEach((marker) => {
      removePulsingEffect(marker);
    });
  }, []);

  const animatePulsingDot = (marker: mapboxgl.Marker, color: string) => {
    const { canvas, render } = createPulsingDot(color);
    const markerElement = marker.getElement();
    const pulsingDot = document.createElement('div');
    pulsingDot.appendChild(canvas);
    pulsingDot.style.position = 'absolute';
    pulsingDot.style.bottom = '-35px';
    pulsingDot.style.left = '50%';
    pulsingDot.style.transform = 'translateX(-50%)';
    markerElement.appendChild(pulsingDot);

    const animate = () => {
      if (render()) {
        animationFrameRef.current = window.requestAnimationFrame(animate);
      }
    };

    animate();
    return pulsingDot;
  };

  const updateMarkers = useCallback(() => {
    console.log('🔄 updateMarkers called', {
      mapExists: !!map.current,
      operationsCount: operations.length,
      mapContainerExists: !!mapContainer.current,
    });

    if (!map.current) {
      console.log('❌ No map instance, skipping updateMarkers');
      return;
    }

    // Check map container visibility and dimensions
    if (mapContainer.current) {
      const rect = mapContainer.current.getBoundingClientRect();
      const styles = window.getComputedStyle(mapContainer.current);
      console.log('📐 Map container info:', {
        width: rect.width,
        height: rect.height,
        display: styles.display,
        visibility: styles.visibility,
        opacity: styles.opacity,
        position: styles.position,
      });
    }

    // Clear existing markers
    console.log(`🗑️ Clearing ${markersRef.current.length} existing markers`);
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Don't proceed if there are no operations
    if (!operations.length) {
      console.log('⚠️ No operations to display');
      return;
    }

    // Add markers for each operation
    operations.forEach((operation) => {
      if (!operation.centroidLocation) return;
      const { latitude, longitude } = operation.centroidLocation;
      const color =
        operation.performance === 'Perfect'
          ? '34, 197, 94' // green-500
          : operation.performance === 'Good'
            ? '59, 130, 246' // blue-500
            : operation.performance === 'Regular'
              ? '234, 179, 8' // yellow-500
              : operation.performance === 'Concerning'
                ? '249, 115, 22' // orange-500
                : '239, 68, 68'; // red-500

      const marker = new mapboxgl.Marker({
        element: createTruckMarker(`rgb(${color})`),
        anchor: 'bottom',
      })
        .setLngLat([longitude, latitude])
        .addTo(map.current!);

      // Add click handler to the marker
      const handleMarkerClick = () => {
        onMarkerClick(operation.id);
        removeAllPulsingEffects();

        map.current?.flyTo({
          center: [longitude, latitude],
          zoom: 15,
        });

        animatePulsingDot(marker, color);
      };

      marker.getElement().addEventListener('click', handleMarkerClick);
      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    const bounds = calculateBounds(
      operations.map((operation) => ({
        centroidLocation: {
          latitude: operation.centroidLocation?.latitude || 0,
          longitude: operation.centroidLocation?.longitude || 0,
        },
      }))
    );
    if (bounds && map.current) {
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
      });
    }

    console.log(`✅ updateMarkers completed: ${markersRef.current.length} markers added`);

    // Check if map is still valid after update
    if (map.current) {
      console.log('🗺️ Map still valid after update:', {
        isStyleLoaded: map.current.isStyleLoaded(),
        isMoving: map.current.isMoving(),
        loaded: map.current.loaded(),
      });

      // Force map to redraw if it's not fully loaded
      if (!map.current.loaded() || map.current.isMoving()) {
        console.log('🔄 Forcing map redraw...');
        // Wait a bit for the map to finish moving, then force redraw
        window.setTimeout(() => {
          if (map.current) {
            map.current.resize();
            map.current.triggerRepaint();
            console.log('✅ Map redraw completed');
          }
        }, 100);
      }
    }
  }, [operations, onMarkerClick, removeAllPulsingEffects]);

  // Initialize map only once - wait for container to be available
  useEffect(() => {
    console.log('🗺️ Map initialization useEffect triggered', {
      mapContainerExists: !!mapContainer.current,
      mapExists: !!map.current,
    });

    if (!mapContainer.current) {
      console.log('⏳ Map container not ready, will retry...');
      return;
    }

    if (map.current) {
      console.log('⏭️ Map already exists, skipping initialization');
      return;
    }

    console.log('🚀 Creating new map instance');
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-58.381592, -34.603722],
      zoom: 12,
    });

    // Wait for map to be ready before adding markers
    map.current.on('load', () => {
      console.log('🎯 Map loaded, calling updateMarkers');
      updateMarkers();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainer.current]); // Depend on mapContainer.current

  // Update markers when operations change
  useEffect(() => {
    console.log('🔄 Operations change useEffect triggered', {
      mapExists: !!map.current,
      operationsCount: operations.length,
    });

    if (!map.current) {
      console.log('❌ No map instance, skipping marker update');
      return;
    }

    console.log('📍 Updating markers due to operations change');
    updateMarkers();
  }, [operations, updateMarkers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        // eslint-disable-next-line no-undef
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  const refreshExtent = () => {
    // Method to refresh the extent of the map
    if (!map.current || !operations.length) return;

    // Fit bounds to show all markers
    const bounds = calculateBounds(
      operations.map((operation) => ({
        centroidLocation: {
          latitude: operation.centroidLocation?.latitude || 0,
          longitude: operation.centroidLocation?.longitude || 0,
        },
      }))
    );

    if (bounds) {
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
      });
    }
  };

  const flyToOperation = (operationId: string) => {
    const operation = operations.find((op) => op.id === operationId);
    if (!operation || !operation.centroidLocation || !map.current) return;

    const marker = markersRef.current.find((m: mapboxgl.Marker) => {
      const markerLngLat = m.getLngLat();
      if (!operation.centroidLocation) return false; // TODO: chECK THIS
      return (
        markerLngLat.lng === operation.centroidLocation.longitude &&
        markerLngLat.lat === operation.centroidLocation.latitude
      );
    });

    if (marker) {
      removeAllPulsingEffects();
      const color =
        operation.performance === 'Perfect'
          ? '34, 197, 94'
          : operation.performance === 'Good'
            ? '59, 130, 246'
            : operation.performance === 'Regular'
              ? '234, 179, 8'
              : operation.performance === 'Concerning'
                ? '249, 115, 22'
                : '239, 68, 68';

      animatePulsingDot(marker, color);

      map.current.flyTo({
        center: [operation.centroidLocation.longitude, operation.centroidLocation.latitude],
        zoom: 15,
      });
    }
  };

  return {
    mapContainer,
    flyToOperation,
    refreshExtent,
  };
};
